import { useEffect, useState } from "react";
import { HttpMethod, callApi } from "src/services/apiService";

// One billing cycle's usage volumes + billed-$ per product. Products without
// a usage-volume metric (Branding, Litigator, Compliance Guide, Data,
// Portability) carry only the *Billed fields.
interface IMonthlyUsage {
  month: string;
  cycleStart: string;
  cycleEnd: string;
  dncScrubs: number;
  reassignedNumbers: number;
  trustCallMonitored: number;
  remediationNumbers: number;
  portalLogins: number;
  dncScrubBilled: number;
  reassignedBilled: number;
  trustCallBilled: number;
  remediationBilled: number;
  callBrandingBilled: number;
  litigatorBilled: number;
  complianceGuideBilled: number;
  dataBilled: number;
  portabilityBilled: number;
  invoiceTotal: number;
}

// Payload from sales/lead-lookup/SalesSummary (camelCase by default policy).
interface ISalesSummary {
  acctId: string;
  customerSince: string | null;
  basePlan: string | null;
  services: string[];
  trailingRevenue12Mo: number;
  monthlyHistory: IMonthlyUsage[];
}

// One product row in the 6-month grid. `volume` picks the usage-count field
// (null for $-only products); `billed` picks the billed-$ field.
interface ProductRow {
  label: string;
  volume: ((m: IMonthlyUsage) => number) | null;
  billed: (m: IMonthlyUsage) => number;
}

const PRODUCT_ROWS: ProductRow[] = [
  { label: "DNC Scrub", volume: (m) => m.dncScrubs, billed: (m) => m.dncScrubBilled },
  { label: "Reassigned", volume: (m) => m.reassignedNumbers, billed: (m) => m.reassignedBilled },
  { label: "TrustCall Monitor", volume: (m) => m.trustCallMonitored, billed: (m) => m.trustCallBilled },
  { label: "Remediation", volume: (m) => m.remediationNumbers, billed: (m) => m.remediationBilled },
  { label: "Call Branding", volume: null, billed: (m) => m.callBrandingBilled },
  { label: "Litigator", volume: null, billed: (m) => m.litigatorBilled },
  { label: "Compliance Guide", volume: null, billed: (m) => m.complianceGuideBilled },
  { label: "Data", volume: null, billed: (m) => m.dataBilled },
  { label: "Portability", volume: null, billed: (m) => m.portabilityBilled },
];

// Compact volume formatting: 21706 → "21.7k", 1253407 → "1.3M".
function formatVolume(n: number): string {
  if (n === 0) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toLocaleString("en-US");
}

// Short month label: "2026-06" → "Jun '26".
function formatMonthLabel(yearMonth: string): string {
  const [y, m] = yearMonth.split("-");
  const date = new Date(Number(y), Number(m) - 1, 1);
  return date.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}

interface AccountSalesSummaryProps {
  customerAcctId: string;
  // Sysadmin impersonation tail (e.g. "&asUser=Some%20Rep"); "" for the
  // normal case. Same convention AccountUsagePanel uses.
  asUserSuffix?: string;
}

const usd = (n: number) =>
  n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

// "Customer since" date in long form (e.g. "June 2019").
function formatCustomerSince(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
  });
}

// Whole-month tenure derived from the customer-since date, rendered as
// "6 yrs 11 mos". Done client-side so it doesn't depend on the server clock.
function formatTenure(iso: string): string {
  const start = new Date(iso);
  const now = new Date();
  let months =
    (now.getFullYear() - start.getFullYear()) * 12 +
    (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) months -= 1;
  if (months < 0) months = 0;

  const years = Math.floor(months / 12);
  const rem = months % 12;
  const parts: string[] = [];
  if (years > 0) parts.push(`${years} yr${years === 1 ? "" : "s"}`);
  if (rem > 0) parts.push(`${rem} mo${rem === 1 ? "" : "s"}`);
  return parts.length > 0 ? parts.join(" ") : "< 1 mo";
}

/**
 * 6-month usage + billing grid for account reviews. Products are rows, billing
 * cycles are columns. Each cell shows the usage volume (where the product has a
 * count metric) above the billed $ for that cycle. Product rows with no
 * activity across the whole window are hidden so the grid stays focused on what
 * the account actually uses.
 */
const MonthlyHistoryGrid: React.FC<{ history: IMonthlyUsage[] }> = ({
  history,
}) => {
  if (!history || history.length === 0) return null;

  // Hide products that are zero across every cycle (volume and billed).
  const activeRows = PRODUCT_ROWS.filter((row) =>
    history.some(
      (m) => row.billed(m) !== 0 || (row.volume != null && row.volume(m) !== 0),
    ),
  );

  return (
    <>
      <div className="fw-bold mt-3 mb-1">Usage &amp; Billing — Last 6 Months</div>
      <div className="text-muted mb-2" style={{ fontSize: "0.75rem" }}>
        Volume over billed amount, per billing cycle.
      </div>
      <div className="table-responsive">
        <table className="table table-sm table-bordered mb-0" style={{ fontSize: "0.8rem" }}>
          <thead>
            <tr>
              <th>Product</th>
              {history.map((m) => (
                <th key={m.month} className="text-end text-nowrap">
                  {formatMonthLabel(m.month)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {activeRows.map((row) => (
              <tr key={row.label}>
                <td className="text-nowrap">{row.label}</td>
                {history.map((m) => (
                  <td key={m.month} className="text-end text-nowrap">
                    {row.volume != null && (
                      <div>{formatVolume(row.volume(m))}</div>
                    )}
                    <div className={row.volume != null ? "text-muted" : ""}>
                      {row.billed(m) > 0 ? usd(row.billed(m)) : "—"}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
            <tr className="fw-bold">
              <td className="text-nowrap">Invoice Total</td>
              {history.map((m) => (
                <td key={m.month} className="text-end text-nowrap">
                  {usd(m.invoiceTotal)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
};

/**
 * Sales summary card for the Lead Lookup expand-row: the account's current
 * CCC services with tenure, and trailing-12-month revenue across all
 * products. Backed by the ownership-gated sales/lead-lookup/SalesSummary
 * endpoint, so a rep only sees accounts they're the salesRep on.
 */
const AccountSalesSummary: React.FC<AccountSalesSummaryProps> = ({
  customerAcctId,
  asUserSuffix,
}) => {
  const [summary, setSummary] = useState<ISalesSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const response = (await callApi(
          `sales/lead-lookup/SalesSummary?customerAcctId=${encodeURIComponent(
            customerAcctId,
          )}${asUserSuffix ?? ""}`,
          HttpMethod.GET,
        )) as ISalesSummary;
        setSummary(response);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch sales summary:", err);
        setError("Failed to load account summary.");
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerAcctId]);

  return (
    <div className="card mb-3">
      <div className="card-header">Account Summary</div>
      <div className="card-body">
        {loading ? (
          <span
            className="spinner-border spinner-border-sm text-secondary"
            role="status"
            aria-label="Loading account summary"
          />
        ) : error ? (
          <span className="text-muted">{error}</span>
        ) : summary ? (
          <>
            <div className="fw-bold mb-1">Current CCC Services</div>
            {summary.services.length > 0 ? (
              <div className="d-flex flex-wrap gap-1 mb-1">
                {summary.services.map((s) => (
                  <span key={s} className="badge bg-primary">
                    {s}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-muted mb-1">No active services.</p>
            )}
            {summary.customerSince && (
              <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                Customer since {formatCustomerSince(summary.customerSince)} —{" "}
                {formatTenure(summary.customerSince)}
              </div>
            )}

            <div className="fw-bold mt-3 mb-1">
              Revenue — Past 12 Months (all products)
            </div>
            <div>{usd(summary.trailingRevenue12Mo)}</div>

            <MonthlyHistoryGrid history={summary.monthlyHistory} />
          </>
        ) : null}
      </div>
    </div>
  );
};

export default AccountSalesSummary;
